/**
 * BoardSquare Component
 *
 * Renders a single cell on the game board. Handles blank, island, and monster
 * display with selection and possible-move styling. Fully controlled via props.
 */

import React, { useCallback } from 'react';
import { COLORS } from '../../../utils/colors';
import { getMonster } from '../../../utils/images';
import { getMonsterNames } from '../../utils/constants.ts';
import type { BoardSquareProps } from '../../types/board.types';
import type { Theme, PlayerColor, PlayerLetter } from '../../types/game.types';
import './Board.css';

/** Extended props for piece border color (optional; used when Board passes player colors). */
type BoardSquarePropsWithColors = BoardSquareProps & {
    playerAColor?: PlayerColor | null;
    playerBColor?: PlayerColor | null;
};

const DEFAULT_THEME: Theme = 'ocean';

/**
 * Resolves monster image filename for the piece (e.g. leviathan_game.png or general_game.png).
 */
function getMonsterImageFilename(monsterTypeId: number | null, imageUrl?: string): string {
    if (imageUrl) return imageUrl;
    const info = monsterTypeId != null ? getMonsterNames(monsterTypeId) : null;
    const slug = info?.url ?? 'general';
    return `${slug}_game.png`;
}

/**
 * If this square is an opponent monster, use the generic monster image.
 */
function getMonsterImageFilenameWithPerspective(
    monsterTypeId: number | null,
    imageUrl: string | undefined,
    isOpponentMonster: boolean
): string {
    if (isOpponentMonster) return 'general_game.png';
    return getMonsterImageFilename(monsterTypeId, imageUrl);
}

/**
 * Returns inline styles for the square background and border based on type and state.
 */
function getSquareStyle(
    square: BoardSquarePropsWithColors['square'],
    selected: boolean,
    history: boolean,
    possible: boolean,
    theme: Theme
): React.CSSProperties {
    const { type } = square;

    if (type === 'island') {
        const light = COLORS.light[theme];
        if (selected) {
            return {
                backgroundColor: light,
                borderColor: COLORS.selected[theme],
                borderWidth: '0.25vh',
            };
        }
        if (possible) {
            return {
                backgroundColor: light,
                borderColor: COLORS.medium[theme],
                borderWidth: '0.25vh',
            };
        }
        if (history) {
            return {
                backgroundColor: COLORS.history,
                borderColor: light,
                borderWidth: '0.25vh',
            };
        }
        return {
            backgroundColor: light,
            borderColor: light,
            borderWidth: '0.25vh',
        };
    }

    if (possible && selected) {
        return { backgroundColor: COLORS.selected[theme] };
    }
    if (possible) {
        return { backgroundColor: COLORS.possible_square[theme] };
    }
    if (history) {
        return { backgroundColor: COLORS.history };
    }

    return {};
}

/**
 * Returns border style for the monster piece (player color or selected highlight).
 */
function getPieceBorderStyle(
    player: BoardSquarePropsWithColors['square']['player'],
    selectedPiece: boolean,
    theme: Theme,
    playerAColor: PlayerColor | null,
    playerBColor: PlayerColor | null
): React.CSSProperties {
    const colorKey = player === 'A' ? playerAColor : playerBColor;
    const borderColor = selectedPiece
        ? COLORS.selected[theme]
        : (colorKey && COLORS.player_colors[colorKey]) || COLORS.player_colors.red;
    return { border: `medium solid ${borderColor}` };
}

export const BoardSquare: React.FC<BoardSquarePropsWithColors> = ({
    square,
    onClick,
    onHover,
    selected = false,
    history = false,
    possible = false,
    selectedPiece = false,
    disabled = false,
    theme = DEFAULT_THEME,
    playerAColor = null,
    playerBColor = null,
    viewerPlayerLetter = null,
}) => {
    const themeKey = (theme as Theme) || DEFAULT_THEME;
    const handleClick = useCallback(() => {
        if (!disabled && onClick) onClick(square);
    }, [disabled, onClick, square]);

    const handleMouseEnter = useCallback(() => {
        if (onHover) onHover(square);
    }, [onHover, square]);

    const handleMouseLeave = useCallback(() => {
        if (onHover) onHover(null);
    }, [onHover]);

    const style = getSquareStyle(square, selected, history, possible, themeKey);
    const isMonster = square.type === 'monster';
    const isOpponentMonster =
        isMonster &&
        viewerPlayerLetter != null &&
        square.player != null &&
        square.player !== (viewerPlayerLetter as PlayerLetter);

    return (
        <div
            className="square_container"
            style={style}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            role="button"
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(e) => {
                if (!disabled && onClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onClick(square);
                }
            }}
            aria-pressed={selected || selectedPiece}
            aria-disabled={disabled}
        >
            {isMonster && (
                <div
                    className="piece_image_container"
                    style={getPieceBorderStyle(
                        square.player,
                        selectedPiece,
                        themeKey,
                        playerAColor,
                        playerBColor
                    )}
                >
                    <div className="piece_background">
                        <div
                            className="piece_image"
                            style={{
                                backgroundImage: `url(${getMonster(
                                    themeKey,
                                    getMonsterImageFilenameWithPerspective(
                                        square.value,
                                        square.imageUrl,
                                        isOpponentMonster
                                    )
                                )})`,
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default BoardSquare;
